import { useLingui } from '@lingui/react/macro'
import ERC20 from 'abis/ERC20'
import TransactionButton from '@/components/ui/transaction-button'
import { Separator } from '@/components/ui/separator'
import { Modal, ModalProps } from 'components'
import useContractWrite from 'hooks/useContractWrite'
import useWatchTransaction from 'hooks/useWatchTransaction'
import { useAtomValue } from 'jotai'
import { chainIdAtom, walletAtom } from 'state/atoms'
import { Allowance } from 'types'
import TransactionConfirmedModal from './TransactionConfirmedModal'
import TransactionError from './TransactionError'
import { useMemo } from 'react'
import { useWatchReadContract } from 'hooks/useWatchReadContract'
import { UseSimulateContractParameters } from 'wagmi'

export interface ITransactionModal extends Omit<ModalProps, 'onChange'> {
  title: string
  description: string
  children: React.ReactNode
  call?: UseSimulateContractParameters
  requiredAllowance?: Allowance
  confirmLabel: string
  onClose: () => void
  onChange?(isLoading: boolean): void
  disabled?: boolean
}

const Approval = ({
  data: { token, spender, symbol, amount },
}: {
  data: Allowance
}) => {
  const { t } = useLingui()
  const { write, isLoading, isReady, hash, gas } = useContractWrite({
    address: token,
    abi: ERC20,
    functionName: 'approve',
    args: [spender, (amount * 120n) / 100n],
  })

  return (
    <>
      <Separator className="-mx-4 my-6" />
      <TransactionButton
        loading={isLoading || !!hash}
        loadingText={
          hash ? t`Waiting for allowance...` : t`Sign in wallet...`
        }
        onClick={write}
        disabled={!isReady}
        text={
          !isReady ? t`Verifying allowance...` : t`Allow use of ${symbol}`
        }
        className="w-full"
        gas={gas}
      />
    </>
  )
}

const useHasAllowance = (allowance: Allowance | undefined) => {
  const account = useAtomValue(walletAtom)
  const chainId = useAtomValue(chainIdAtom)

  const { data } = useWatchReadContract(
    allowance && account
      ? {
          abi: ERC20,
          functionName: 'allowance',
          address: allowance.token,
          args: [account, allowance.spender],
          chainId,
        }
      : undefined
  )

  if (!allowance) {
    return true
  }

  return (data ?? 0n) >= allowance.amount
}

const TransactionModal = ({
  title,
  requiredAllowance,
  call,
  children,
  disabled,
  description,
  confirmLabel,
  onClose,
  onChange = () => {},
  ...props
}: ITransactionModal) => {
  const { t } = useLingui()
  const hasAllowance = useHasAllowance(requiredAllowance)
  const {
    isLoading,
    write,
    hash,
    isIdle,
    status,
    validationError,
    error,
    isReady,
    gas,
    reset,
  } = useContractWrite(hasAllowance && !disabled ? call : undefined)
  useWatchTransaction({ hash, label: description })

  const validationMessage = useMemo(() => {
    if (validationError) {
      if (validationError.message.indexOf('empty redemption') !== -1) {
        return t`Current basket not capitalized, please try to redeem with a previous basket.`
      }
    }

    return null
  }, [validationError?.message, t])

  const handleConfirm = () => {
    if (write) {
      onChange(true)
      write()
    }
  }

  const handleRetry = () => {
    reset()
    onChange(false)
  }

  if (hash) {
    return <TransactionConfirmedModal hash={hash} onClose={onClose} />
  }

  const isPreparing = hasAllowance && call && !isReady

  return (
    <Modal title={title} onClose={onClose} {...props}>
      {status === 'error' && (
        <TransactionError
          title={t`Transaction failed`}
          subtitle={error?.message}
          onClose={handleRetry}
        />
      )}
      {children}
      {requiredAllowance && !hasAllowance && (
        <Approval data={requiredAllowance} />
      )}
      <Separator className="-mx-4 mt-6" />
      <TransactionButton
        loading={isLoading || isPreparing}
        disabled={!isReady || disabled}
        loadingText={
          isPreparing ? t`Preparing transaction` : t`Pending, sign in wallet`
        }
        text={confirmLabel}
        onClick={handleConfirm}
        className="w-full mt-4"
        gas={hasAllowance ? gas : undefined}
      />
      {!!validationMessage && (
        <div className="text-center text-sm mt-4">
          <span className="text-destructive">{validationMessage}</span>
        </div>
      )}
    </Modal>
  )
}

export default TransactionModal
